'use client';

import { useEffect } from 'react';
import AOS from 'aos';

import 'aos/dist/aos.css';

/**
 * Hook/helper to initialize AOS with performance optimizations
 * and accessibility considerations.
 *
 * Important:
 * - Keeps useAOS() backward-compatible for the rest of the website.
 * - Adds refreshAOS() for components that render content asynchronously.
 */

function canUseDOM() {
  return typeof window !== 'undefined' && typeof document !== 'undefined';
}

function shouldEnableAOS() {
  if (!canUseDOM()) {
    return false;
  }

  return !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

function revealAOSContentWithoutAnimation() {
  if (!canUseDOM()) {
    return;
  }

  document.querySelectorAll<HTMLElement>('[data-aos]').forEach((element) => {
    element.classList.add('aos-animate');

    /**
     * Defensive fallback:
     * In rare cases, AOS CSS may have hidden the element before
     * JS decided animations should be disabled.
     */
    element.style.opacity = '1';
    element.style.transform = 'none';
    element.style.transition = 'none';
  });
}

/**
 * Safe refresh for dynamically rendered AOS elements.
 * Use this after async data is loaded/rendered.
 */
export function refreshAOS() {
  if (!canUseDOM()) {
    return;
  }

  if (!shouldEnableAOS()) {
    revealAOSContentWithoutAnimation();
    return;
  }

  window.requestAnimationFrame(() => {
    try {
      AOS.refreshHard();
    } catch {
      revealAOSContentWithoutAnimation();
    }
  });
}

export function useAOS() {
  useEffect(() => {
    if (!canUseDOM()) {
      return;
    }

    if (!shouldEnableAOS()) {
      revealAOSContentWithoutAnimation();
      return;
    }

    const isMobile = window.innerWidth < 1024;

    AOS.init({
      duration: isMobile ? 450 : 800,
      easing: 'ease-out-cubic',
      once: true,

      /**
       * More reliable on mobile than "bottom-bottom".
       * "bottom-bottom" can wait too long on small screens.
       */
      anchorPlacement: 'top-bottom',

      /**
       * Important:
       * Keep MutationObserver enabled.
       * Some components render data-aos elements after async fetch.
       * If this is disabled on mobile, those elements may never receive
       * .aos-animate and can stay invisible.
       */
      disableMutationObserver: false,

      /**
       * Lower offset helps mobile content appear earlier.
       */
      offset: isMobile ? 40 : 80,

      /**
       * Avoid global delay. Individual components can still set data-aos-delay.
       */
      delay: 0,
    });

    /**
     * Initial refresh after layout settles.
     * Safe for other components using the same hook.
     */
    const timeoutId = window.setTimeout(() => {
      refreshAOS();
    }, 250);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, []);
}